import React, { useCallback, useEffect, useState } from 'react'
import { GetAction } from '../../Component/GetAction'
import { PostAction } from '../../Component/PostAction'
import { PostFormAction } from '../../Component/PostFormAction'
import ManageTable from '../ManageTable'
import { RequireModule, PageHeader, BTN_PRIMARY } from './shared'
import { mediaUrl } from '../../utils/mediaUrl'

const EMPTY = {
  banner_id: '',
  title: '',
  subtitle: '',
  sort_order: 0,
  status: 1,
  file: null,
}

function HeroBannerPage() {
  const [rows, setRows] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [preview, setPreview] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const data = await GetAction('get', {}, 'hero_banner/bannerListAll')
    if (data?.status === 'success' && Array.isArray(data.response)) {
      setRows(data.response)
      setError('')
    } else {
      setError(data?.message || 'Failed to load banners.')
      setRows([])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openNew = () => {
    setForm(EMPTY)
    setPreview('')
    setShowForm(true)
    setMessage(null)
  }

  const openEdit = (row) => {
    setForm({
      banner_id: row.banner_id || '',
      title: row.title || '',
      subtitle: row.subtitle || '',
      sort_order: row.sort_order ?? 0,
      status: Number(row.status) === 1 ? 1 : 0,
      file: null,
    })
    setPreview(mediaUrl(row.image_path || ''))
    setShowForm(true)
    setMessage(null)
  }

  const onFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setForm((p) => ({ ...p, file }))
    setPreview(URL.createObjectURL(file))
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!form.banner_id && !form.file) {
      setMessage({ error: true, text: 'Banner image is required.' })
      return
    }
    setSaving(true)
    setMessage(null)
    const fd = new FormData()
    if (form.banner_id) fd.append('banner_id', form.banner_id)
    fd.append('title', form.title.trim())
    fd.append('subtitle', form.subtitle.trim())
    fd.append('sort_order', String(form.sort_order ?? 0))
    fd.append('status', String(form.status))
    if (form.file) fd.append('banner_image', form.file)

    const data = await PostFormAction('post', fd, 'hero_banner/addBanner')
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Banner saved.' })
      setShowForm(false)
      await load()
    } else {
      setMessage({
        error: true,
        text: data?.message || data?.data?.message || 'Failed to save banner.',
      })
    }
    setSaving(false)
  }

  const onDelete = async (bannerId) => {
    if (!window.confirm('Delete this banner?')) return
    const data = await PostAction('post', { banner_id: bannerId }, 'hero_banner/deleteBanner')
    setMessage({
      error: data?.status !== 'success',
      text: data?.message || (data?.status === 'success' ? 'Deleted.' : 'Failed.'),
    })
    if (data?.status === 'success') await load()
  }

  const columns = [
    { key: 'index', label: '#', width: '50px' },
    {
      key: 'image',
      label: 'Image',
      render: (row) =>
        row.image_path ? (
          <img
            src={mediaUrl(row.image_path)}
            alt={row.title || 'Banner'}
            className="manage-thumb"
          />
        ) : (
          '—'
        ),
    },
    { key: 'title', label: 'Title' },
    { key: 'subtitle', label: 'Subtitle' },
    {
      key: 'sort_order',
      label: 'Order',
      render: (row) => row.sort_order ?? 0,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (Number(row.status) === 1 ? 'Active' : 'Inactive'),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="d-flex">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary mr-2"
            onClick={() => openEdit(row)}
          >
            Edit
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => onDelete(row.banner_id)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ]

  return (
    <RequireModule moduleKey="hero_banner">
      <PageHeader
        title="Hero Banner"
        subtitle="Homepage carousel images."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={BTN_PRIMARY}
            onClick={openNew}
          >
            Add Banner
          </button>
        }
      />
      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      {showForm ? (
        <div className="manage-card mb-4">
          <h2 className="h5 mb-3">
            {form.banner_id ? 'Edit Banner' : 'Add Banner'}
          </h2>
          <form onSubmit={onSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="hb_title">
                  Title
                </label>
                <input
                  id="hb_title"
                  className="form-control"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="hb_subtitle">
                  Subtitle
                </label>
                <input
                  id="hb_subtitle"
                  className="form-control"
                  value={form.subtitle}
                  onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="hb_order">
                  Sort Order
                </label>
                <input
                  id="hb_order"
                  type="number"
                  className="form-control"
                  value={form.sort_order}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, sort_order: Number(e.target.value) }))
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="hb_status">
                  Status
                </label>
                <select
                  id="hb_status"
                  className="form-control"
                  value={form.status}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, status: Number(e.target.value) }))
                  }
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
              <div className="col-12 mb-3">
                <label className="form-label" htmlFor="hb_image">
                  Banner Image {!form.banner_id ? '*' : '(leave empty to keep)'}
                </label>
                <input
                  id="hb_image"
                  type="file"
                  accept="image/*"
                  className="form-control"
                  required={!form.banner_id}
                  onChange={onFile}
                />
                {preview ? (
                  <img src={preview} alt="Preview" className="manage-preview mt-2" />
                ) : null}
              </div>
            </div>
            <div className="d-flex">
              <button
                type="submit"
                className="btn btn-primary mr-2"
                style={BTN_PRIMARY}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Banner'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="manage-card mb-4">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <ManageTable
            columns={columns}
            rows={rows}
            searchKeys={['title', 'subtitle']}
          />
        )}
      </div>
    </RequireModule>
  )
}

export default HeroBannerPage
